import { useCallback, useState, useEffect } from 'react';
import { useQuery, useMutation, useApolloClient } from 'react-apollo';
import { useDispatch } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import qs from 'query-string';

import {
  accountUpdate as accountUpdateMutation,
  passwordCreate as passwordCreateMutation,
  accountLoginByEmail as accountLoginByEmailMutation,
  emailAuthenticationRequest as emailAuthenticationRequestMutation,
  photoManagerPurchaseReminderSet as photoManagerPurchaseReminderSetMutation,
} from '../../../../graphql/mutations/account';
import {
  checkoutPhotoManagerSubscription as checkoutPhotoManagerSubscriptionMutation,
  checkoutPhotoFinderCredits as checkoutPhotoFinderCreditsMutation,
} from '../../../../graphql/mutations/checkout';
import { openAlert } from '../../../../actions/modal-actions';
import { getErrorString } from '../../../../util/normalize-errors';
import { sentryCaptureMessage } from '../../../../util/sentry';
import { loginSuccess } from '../../../../actions/auth-actions';
import { ACCOUNT_PRO_ROLES } from '../../../../constants/account-roles';
import getPhotoManagerSubscriptionItems from '../../../../graphql/queries/getPhotoManagerSubscriptionItems';
import {
  getProRegistrationStoreKeyValue,
  PRO_REGISTRATION_PERSONAS,
  PRO_REGISTRATION_STEP,
} from '../helpers';
import { AccountPromoCodeVerify } from '../../../../graphql/queries/account-referrals';
import { gtm } from '../../../../util/analytics';
import {
  accountEmailExists as accountEmailExistsQuery,
  accountUsernameExists as accountUsernameExistsQuery,
  accountPhoneNumberExists as accountPhoneNumberExistsQuery,
  MeQuery,
} from '../../../../graphql/queries/account';
import { PHOTO_MANAGER_SUBSCRIPTION_BILLING_PERIOD } from '../../../../apps/pro/views/Account/AccountPhotoManagerSettings/components/PhotoManagerSubscriptionCheckout/helpers';
import { ACCOUNT_REFERRAL_TYPE } from '../../../../constants/account-referrals';
import { formatPriceForDisplay } from '../../../../util/format-money';
import { getRoleBasename } from '../../../../util/role-definitions';
import usePromoCode from '../../../../components/global/PromoCodeInput/usePromoCodeParse';
import { getPlicOauthUrl } from '../../../../components/global/PLICBooksLogin/helpers';
import { accountReferralCreate as accountReferralCreateMutation } from '../../../../graphql/mutations/account-referrals';
import getPhotoFinderCreditItems from '../../../../apps/pro/views/Account/AccountPhotoManagerSettings/components/PhotoFinderCreditItemsCheckout/queries/getPhotoFinderCreditItems';
import { isNativeAppWebView } from '../../../../services/native-bridge/native-apps-interface';
import { PURCHASE_TYPE } from '../../../../components/global/ThankYouView/constants';
import { EMAIL_AUTH_INTENT } from '../../../../constants/auth';
import useOpenModal from '../../../../hooks/useOpenModal';
import { THANK_YOU_MODAL } from '../../../../components/global/ThankYouView';
import { SESSION_STATE } from '../../../../apps/shutterbug/views/SignupAlbumCreate/helpers';
import useCrazyEggConversions from '../../../../services/crazy-egg/hooks/useCrazyEggConversions';
import { CRAZY_EGG_CONVERSION_EVENTS } from '../../../../services/crazy-egg/constants';

// Tiers now amount to a given amount of credits
const PHOTO_FINDER_TIERS = {
  TIER_1: 1000,
  TIER_2: 5000,
  TIER_3: 10000,
  TIER_4: 25000,
  TIER_5: 50000,
  TIER_6: 100000,
};
// Promo code utilized to provide users coming from waldo PRO with 1 year free.
const WALDO_PRO_FREE_PROMO_CODE = 'WALDOPRO1YFREE';
/**
 * Pro registration hook. Performs the entire flow & registration process.
 *
 * @returns {{
 *  loading: Boolean,
 *  defaultPersona: String,
 *  photoManagerSubscriptionItems: Array<Object>,
 *  photoFinderSubscriptionItems: Array<Object>,
 *  defaultPhotoFinderTier: String,
 *  defaultPhotoManagerItem: Object,
 *  activeStep: String,
 *  canGoBack: Boolean,
 *  loadingSubscriptionItems: Boolean,
 *  accountPromoCode: Object,
 *  onSubmit: Function,
 *  onAccountPromoCodeVerify: Function,
 *  onAccountUsernameVerify: Function,
 *  onGoBack: Function
 *  }}
 */
const useProRegister = () => {
  const openModal = useOpenModal();
  const crazyEggConversions = useCrazyEggConversions();
  const [loading, setLoading] = useState(false);
  const client = useApolloClient();
  const location = useLocation();
  const dispatch = useDispatch();
  const onErrorModalOpen = useCallback(state => dispatch(openAlert(state)), [
    dispatch,
  ]);
  const onLogin = useCallback(() => dispatch(loginSuccess()));
  const [accountUpdate] = useMutation(accountUpdateMutation);
  const [passwordCreate] = useMutation(passwordCreateMutation);
  const [emailAuthenticationRequest] = useMutation(
    emailAuthenticationRequestMutation,
  );
  const [accountLoginByEmail] = useMutation(accountLoginByEmailMutation);
  const [checkoutPhotoManagerSubscription] = useMutation(
    checkoutPhotoManagerSubscriptionMutation,
  );
  const [checkoutPhotoFinderCredits] = useMutation(
    checkoutPhotoFinderCreditsMutation,
  );
  const [accountReferralCreate] = useMutation(accountReferralCreateMutation);
  const [isWaldoProFreeRegister, setIsWaldoProFreeRegister] = useState(false);
  const [verifyingDefaultPromoCode, setVerifyingDefaultPromoCode] = useState(
    true,
  );
  const [photoManagerPurchaseReminderSet] = useMutation(
    photoManagerPurchaseReminderSetMutation,
  );
  const history = useHistory();

  const {
    data: { photoManagerSubscriptionItems = [] },
    loading: loadingPhotoManagerSubscriptionItems,
  } = useQuery(getPhotoManagerSubscriptionItems);
  const {
    data: { photoFinderCreditItems = [] } = {},
    loading: loadingPhotoFinderCreditItems,
  } = useQuery(getPhotoFinderCreditItems);

  const onPhotoManagerPurchaseReminderSet = async ({
    sessionState = SESSION_STATE.START,
  }) => {
    await photoManagerPurchaseReminderSet({
      variables: {
        input: {
          session_state: sessionState,
        },
      },
    });
  };

  const {
    role: accountRole = ACCOUNT_PRO_ROLES.PHOTOGRAPHER,
    persona: defaultPersona,
    source = '',
    tier = '',
    billing_period: billingPeriod = PHOTO_MANAGER_SUBSCRIPTION_BILLING_PERIOD.YEARLY.toLowerCase(),
    promo_code: defaultPromoCode,
    event_purchase: eventPurchase,
    plic,
    // Using this will mark the account as having PM waived.
    // use with care.
    is_free: isFree,
    skip_free_trial: skipFreeTrial,
    // We will now remove PF signup in general. If people want they can sign up
    // later as per Rodney.
    skip_photo_finder: skipPhotoFinder = '1',
    photo_finder_credits_code,
    no_marketing: noMarketing,
    // Print Partner Related:
    print_partner_id: printPartnerId,
    // Waldo PRO related
    is_waldo_pro: isWaldoPro,
  } = qs.parse(location.search ?? '') ?? {};

  const skipFreeTrialBool = ['true', '1'].includes(
    (skipFreeTrial ?? '').toLowerCase(),
  );
  const skipPhotoFinderBool = ['true', '1'].includes(
    (skipPhotoFinder ?? '').toLowerCase(),
  );
  const isWaldoProBool = ['true', '1'].includes(
    (isWaldoPro ?? '').toLowerCase(),
  );

  const initialActiveStep = (() => {
    if (eventPurchase) {
      return PRO_REGISTRATION_STEP.EVENT_SKU_SELECTION;
    }

    if (plic && tier && billingPeriod) {
      return PRO_REGISTRATION_STEP.DETAILS;
    }

    if (plic && isFree) {
      return PRO_REGISTRATION_STEP.DETAILS;
    }

    return PRO_REGISTRATION_STEP.PHOTO_MANAGER_SUBSCRIPTION_SELECTION;
  })();

  const [activeStep, setActiveStep] = useState(initialActiveStep);
  const [accountPromoCode, setAccountPromoCode] = useState(null);

  const { onPromoCodeVerify } = usePromoCode({
    onPromoCodeVerified: accountPromoCodeRecord => {
      setAccountPromoCode(accountPromoCodeRecord);
    },
  });

  const onPromoCodeValidate = async promoCode => {
    return onPromoCodeVerify({
      promoCode,
      accountReferralTypes: [
        ACCOUNT_REFERRAL_TYPE.PHOTO_MANAGER_YEARLY_50_PC_OFF_ONCE,
        ACCOUNT_REFERRAL_TYPE.PHOTO_MANAGER_YEARLY,
        ACCOUNT_REFERRAL_TYPE.PHOTO_MANAGER_MONTHLY,
        ACCOUNT_REFERRAL_TYPE.PHOTO_FINDER_YEARLY,
        ACCOUNT_REFERRAL_TYPE.PHOTO_FINDER_MONTHLY,
      ],
    });
  };

  const defaultPhotoFinderTier = tier
    ? photoFinderCreditItems.find(
        item => item.amount === PHOTO_FINDER_TIERS[tier.toUpperCase?.()],
      )
    : null;
  const defaultPhotoManagerItem =
    billingPeriod && ['monthly', 'yearly'].includes(billingPeriod)
      ? photoManagerSubscriptionItems.find(
          item => item.billing_period === billingPeriod.toUpperCase?.(),
        )
      : null;

  useEffect(() => {
    const validatePromoCode = async () => {
      let promoCode;
      if (defaultPromoCode) {
        promoCode = await onPromoCodeValidate(defaultPromoCode);
      } else if (isWaldoProBool) {
        promoCode = await onPromoCodeValidate(WALDO_PRO_FREE_PROMO_CODE);
      }

      if (
        promoCode?.is_eligible &&
        promoCode?.discount?.mode === 'PERCENTAGE' &&
        promoCode?.discount?.value === 100 &&
        isWaldoProBool
      ) {
        setIsWaldoProFreeRegister(true);
      }
      setVerifyingDefaultPromoCode(false);
    };

    if (!defaultPromoCode && !isWaldoProBool) {
      setVerifyingDefaultPromoCode(false);
    } else {
      validatePromoCode();
    }
  }, [defaultPromoCode, isWaldoProBool]);

  /**
   * Verifies a promo code.
   *
   * @param {string} promoCode The promo code.
   * @returns {Promise<boolean|*|boolean>}
   */
  const onAccountPromoCodeVerify = async promoCode => {
    setLoading(true);

    try {
      const { data: { accountPromoCodeVerify } = {} } = await client.query({
        query: AccountPromoCodeVerify,
        variables: {
          promoCode,
        },
      });

      return accountPromoCodeVerify;
    } catch {
      return false;
    } finally {
      setLoading(false);
    }
  };

  const onAccountUsernameVerify = async username => {
    setLoading(true);

    try {
      const {
        data: { accountUsernameExists: accountUsernameExistsResult } = {},
      } = await client.query({
        query: accountUsernameExistsQuery,
        variables: {
          username,
        },
      });

      return accountUsernameExistsResult;
    } catch {
      return false;
    } finally {
      setLoading(false);
    }
  };

  const onAccountEmailVerify = async emailAddress => {
    setLoading(true);

    try {
      const {
        data: { accountEmailExists: accountEmailExistsResult },
      } = await client.query({
        query: accountEmailExistsQuery,
        variables: {
          email_address: emailAddress,
        },
      });
      return accountEmailExistsResult;
    } catch {
      return false;
    } finally {
      setLoading(false);
    }
  };

  const onAccountPhoneNumberVerify = async phoneNumber => {
    setLoading(true);

    try {
      const {
        data: { accountPhoneNumberExists: accountPhoneNumberExistsResult } = {},
      } = await client.query({
        query: accountPhoneNumberExistsQuery,
        variables: {
          phone_number: phoneNumber,
        },
      });

      return accountPhoneNumberExistsResult;
    } catch {
      return false;
    } finally {
      setLoading(false);
    }
  };

  const onEmailPinCodeRequest = async emailAddress => {
    if (loading) {
      return;
    }

    setLoading(true);

    try {
      await emailAuthenticationRequest({
        variables: {
          input: {
            email_address: emailAddress,
            intent: EMAIL_AUTH_INTENT.LOGIN,
            use_pin_code: true,
          },
        },
      });
    } catch (e) {
      onErrorModalOpen({
        message: 'There was an error sending your pin verification',
        info: getErrorString(e),
      });
    } finally {
      setLoading(false);
    }
  };

  const onPasswordCreate = async password => {
    if (loading) {
      return;
    }

    setLoading(true);

    try {
      const { data: { me } = {} } = await client.query({ query: MeQuery });
      await passwordCreate({
        variables: {
          input: {
            account_id: me.id,
            password,
          },
        },
        refetchQueries: [{ query: MeQuery }],
      });
    } catch (e) {
      onErrorModalOpen({
        message: 'There was an error sending your pin verification',
        info: getErrorString(e),
      });
    } finally {
      setLoading(false);
    }
  };

  const onCheckout = async values => {
    if (loading) {
      return;
    }

    setLoading(true);
    try {
      const { data: { me } = {} } = await client.query({ query: MeQuery });
      const accountId = me.id;

      const personaObj = PRO_REGISTRATION_PERSONAS.find(
        p => p.value === values.persona,
      );

      const role = personaObj ? personaObj.role : accountRole;

      const eventCreateUrl = `/app/${getRoleBasename(
        role,
      )}/folders/create?${qs.stringify(
        {
          selectedEventAddonItemIds: [values.event_addon_item_id],
        },
        { arrayFormat: 'bracket' },
      )}`;

      const printPartnerSetupUrl = `/app/${getRoleBasename(
        role,
      )}/account/settings/print-provider`;

      const selectedSubscriptionItem = photoManagerSubscriptionItems?.find(
        item => item.id === values.plan_id,
      );
      const selectedPhotoFinderCreditItem = photoFinderCreditItems?.find(
        item => item.id === values.tier_id,
      );

      // The user is checking out the plan_id
      if (values.stripe_token && !eventPurchase && !isFree) {
        const finalSkipFreeTrial = !!values.tier_id || skipFreeTrialBool;
        await checkoutPhotoManagerSubscription({
          variables: {
            input: {
              photo_manager_subscription_item_id: values.plan_id,
              email_address: values.email_address,
              stripe_token: values.stripe_token.id,
              stripe_payment_method_id:
                values.stripe_token.stripe_payment_method_id,
              stripe_connect_account_id:
                values.stripe_token.stripe_connect_account_id,
              promo_code: values.promo_code || values.default_promo_code,
              // If the user subbed for photo finder, free trial is skipped.
              skip_free_trial: finalSkipFreeTrial,
            },
          },
        });

        if (!isNativeAppWebView()) {
          if (finalSkipFreeTrial) {
            gtm.trackCustomEvent({
              event: 'PM_Purchased',
              pro_subtype:
                personaObj?.value ??
                (getRoleBasename(role) ?? '')?.toUpperCase(),
              user_id: me?.id,
              price: formatPriceForDisplay(
                selectedSubscriptionItem?.price?.value,
              ),
            });
          } else {
            gtm.trackCustomEvent({
              event: 'PM_FT_Started',
              pro_subtype:
                personaObj?.value ??
                (getRoleBasename(role) ?? '')?.toUpperCase(),
              user_id: me?.id,
              price: 0,
            });
          }

          const purchaseMetadata = {
            subscriptionDetails: {
              photoManagerSubscriptionItem: selectedSubscriptionItem,
              freeTrialAvailable: !finalSkipFreeTrial,
            },
            verticalDetails: {
              persona: personaObj,
              basename: getRoleBasename(role),
              role,
            },
          };

          // Crazy Egg Triggers
          if (!finalSkipFreeTrial) {
            crazyEggConversions.onTrackConversionEvent(
              CRAZY_EGG_CONVERSION_EVENTS.PRO_TRIAL,
            );
          }

          openModal(THANK_YOU_MODAL, {
            purchaseType: PURCHASE_TYPE.PHOTO_MANAGER_SUBSCRIPTION,
            purchaseMetadata,
          });
        }

        // The user has checked out with a photo finder subscription
        if (selectedPhotoFinderCreditItem) {
          await checkoutPhotoFinderCredits({
            variables: {
              input: {
                photo_finder_credit_item_id: values.tier_id,
                email_address: values.email_address,
                stripe_token: values.stripe_token.id,
                stripe_payment_method_id:
                  values.stripe_token?.stripe_payment_method_id,
                stripe_connect_account_id:
                  values.stripe_token?.stripe_connect_account_id,
                platform: 'WEB',
              },
            },
          });

          gtm.trackCustomEvent({
            event: 'PhotoFinderCreditsPurchase',
            package: `PF_ITEM_${selectedPhotoFinderCreditItem?.amount}`,
            value: formatPriceForDisplay(
              selectedPhotoFinderCreditItem?.price?.value,
            ),
            user_id: accountId,
          });
        }
      }

      gtm.trackEvent({
        type: 'Register as PRO',
        action: getProRegistrationStoreKeyValue(values),
        label: accountRole ?? 'Pro Photographer',
        source,
      });

      // Forces log in and redirect to /app
      if (plic && isFree) {
        window.location.assign(getPlicOauthUrl('/app'));
        return;
      }

      if (plic) {
        setActiveStep(PRO_REGISTRATION_STEP.PLIC_LOGIN);
        return;
      }

      onLogin();

      if (eventPurchase) {
        history.push(eventCreateUrl);
      }

      if (printPartnerId) {
        history.push(printPartnerSetupUrl);
      }
    } catch (responseError) {
      onErrorModalOpen({
        message: 'There was an error with your registration',
        info: responseError?.request_id ?? getErrorString(responseError),
      });

      sentryCaptureMessage('PRO Registration Error', {
        level: 'error',
        extra: {
          error: responseError,
          email: values.email_address,
        },
      });

      setLoading(false);
    }
  };

  /**
   * Handles the logic of registering and checking out the user.
   *
   * @param {Object} values The regiser form's values.
   * @returns {Promise<void>}
   */
  const onRegister = async values => {
    if (loading) {
      return;
    }

    setLoading(true);

    const personaObj = PRO_REGISTRATION_PERSONAS.find(
      p => p.value === values.persona,
    );

    const role = personaObj ? personaObj.role : accountRole;

    if (!role) {
      return;
    }

    try {
      const {
        data: { accountLoginByEmail: accountLoginByEmailResponse } = {},
      } = await accountLoginByEmail({
        variables: {
          input: {
            email_address: values.email_address,
            email_authentication_code: values.pin_code,
            set_cookie: true,
          },
        },
      });

      if (accountLoginByEmailResponse.success) {
        const accountId = accountLoginByEmailResponse.account?.id;

        gtm.trackCustomEvent({
          event: 'Pro_Account_Created',
          user_id: accountId,
        });

        const clientStoreItems = [
          {
            key: 'pro_registration_mode',
            value: getProRegistrationStoreKeyValue(values),
          },
        ];

        if (plic) {
          await accountReferralCreate({
            variables: {
              input: {
                referral_type: ACCOUNT_REFERRAL_TYPE.PLIC_PRO_SIGNUP,
                origin: 'PLIC_BOOKS',
                platform: 'WEB',
              },
            },
          });
        }

        if (printPartnerId) {
          await accountReferralCreate({
            variables: {
              input: {
                referral_type: ACCOUNT_REFERRAL_TYPE.PRINT_PARTNER,
                origin: printPartnerId,
                platform: 'WEB',
              },
            },
          });
        }

        if (values.persona) {
          clientStoreItems.push({
            key: 'pro_registration_persona',
            value: values.persona,
          });
        }

        await accountUpdate({
          variables: {
            input: {
              id: accountId,
              name: `${values.first_name} ${values.last_name}`.trim(),
              contact_phone: values.contact_phone,
              username: values.username,
              pro_roles: [role.toUpperCase()],
              organization_name: values.organization_name,
              client_store_items: clientStoreItems,
              state_id: values.state_id ?? undefined,
              photo_finder_credits_code:
                // Will only be applied for PLIC situations.
                plic && isFree ? photo_finder_credits_code : undefined,
              pro_settings: printPartnerId
                ? {
                    default_print_partner_id: printPartnerId,
                    locked_print_partner_id: printPartnerId,
                    requires_print_partner_setup: true,
                  }
                : undefined,
            },
          },
          // Ensure the newly added updates are pulled from the server.
          refetchQueries: [{ query: MeQuery }],
        });
      } else {
        throw Error('There was an error signing in');
      }
    } catch (responseError) {
      onErrorModalOpen({
        message: 'There was an error with your registration',
        info: responseError?.request_id ?? getErrorString(responseError),
      });

      sentryCaptureMessage('PRO Registration Error', {
        level: 'error',
        extra: {
          error: responseError,
          email: values.email_address,
        },
      });
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = async (values, { setFieldValue }) => {
    const promoCode = await onPromoCodeValidate(values.promo_code);

    if (promoCode) {
      // Force to set a plan based on the Promo code.
      setFieldValue(
        'plan_id',
        promoCode.type === ACCOUNT_REFERRAL_TYPE.PHOTO_MANAGER_MONTHLY
          ? photoManagerSubscriptionItems.find(
              item =>
                item.billing_period ===
                PHOTO_MANAGER_SUBSCRIPTION_BILLING_PERIOD.MONTHLY,
            )?.id
          : photoManagerSubscriptionItems.find(
              item =>
                item.billing_period ===
                PHOTO_MANAGER_SUBSCRIPTION_BILLING_PERIOD.YEARLY,
            )?.id,
      );
    }

    switch (activeStep) {
      case PRO_REGISTRATION_STEP.FREE_TRIAL: {
        await onEmailPinCodeRequest(values.email_address);
        setActiveStep(PRO_REGISTRATION_STEP.PIN_VERIFICATION);
        return;
      }

      case PRO_REGISTRATION_STEP.EVENT_SKU_SELECTION: {
        setActiveStep(PRO_REGISTRATION_STEP.DETAILS);
        break;
      }

      case PRO_REGISTRATION_STEP.PHOTO_MANAGER_SUBSCRIPTION_SELECTION: {
        if (promoCode?.trial_days || skipPhotoFinderBool) {
          setActiveStep(PRO_REGISTRATION_STEP.DETAILS);
        } else {
          setActiveStep(PRO_REGISTRATION_STEP.PHOTO_FINDER_CREDITS_SELECTION);
        }

        break;
      }

      case PRO_REGISTRATION_STEP.PHOTO_FINDER_CREDITS_SELECTION: {
        setActiveStep(PRO_REGISTRATION_STEP.DETAILS);
        break;
      }

      case PRO_REGISTRATION_STEP.DETAILS: {
        await onEmailPinCodeRequest(values.email_address);
        setActiveStep(PRO_REGISTRATION_STEP.PIN_VERIFICATION);
        break;
      }

      case PRO_REGISTRATION_STEP.PIN_VERIFICATION: {
        await onRegister(values);

        const {
          data: { me },
        } = await client.query({ query: MeQuery });

        if (!me) {
          return;
        }

        if (!me.has_password) {
          setActiveStep(PRO_REGISTRATION_STEP.PASSWORD_CREATION);
          return;
        }

        if (eventPurchase) {
          await onCheckout(values);
          return;
        }

        if (plic && isFree) {
          setActiveStep(PRO_REGISTRATION_STEP.FUNDRAISER_MARKETING_COMMITMENT);
        } else {
          setActiveStep(PRO_REGISTRATION_STEP.CART);
        }
        break;
      }

      case PRO_REGISTRATION_STEP.PASSWORD_CREATION: {
        await onPasswordCreate(values.password);

        if (eventPurchase) {
          await onCheckout(values);
          return;
        }

        if (plic && isFree) {
          setActiveStep(PRO_REGISTRATION_STEP.FUNDRAISER_MARKETING_COMMITMENT);
        } else {
          setActiveStep(PRO_REGISTRATION_STEP.CART);
        }
        break;
      }
      case PRO_REGISTRATION_STEP.CART:
      case PRO_REGISTRATION_STEP.FUNDRAISER_MARKETING_COMMITMENT:
      default: {
        await onCheckout(values);
        break;
      }
    }
  };

  const canGoBack = (() => {
    switch (activeStep) {
      case PRO_REGISTRATION_STEP.DETAILS: {
        return !(plic && tier && billingPeriod) && !(plic && isFree);
      }
      case PRO_REGISTRATION_STEP.PIN_VERIFICATION:
        return true;
      default:
        return false;
    }
  })();

  const onGoBack = () => {
    switch (activeStep) {
      case PRO_REGISTRATION_STEP.DETAILS:
        if (eventPurchase) {
          setActiveStep(PRO_REGISTRATION_STEP.EVENT_SKU_SELECTION);
        } else {
          setActiveStep(
            PRO_REGISTRATION_STEP.PHOTO_MANAGER_SUBSCRIPTION_SELECTION,
          );
        }

        break;
      case PRO_REGISTRATION_STEP.PIN_VERIFICATION:
        setActiveStep(PRO_REGISTRATION_STEP.DETAILS);

        break;
      default:
        break;
    }
  };

  return {
    loading,
    defaultPersona: plic ? 'school' : defaultPersona,
    photoManagerSubscriptionItems,
    photoFinderCreditItems,
    defaultPhotoFinderTier,
    defaultPhotoManagerItem,
    activeStep,
    canGoBack,
    loadingSubscriptionItems:
      loadingPhotoFinderCreditItems || loadingPhotoManagerSubscriptionItems,
    accountPromoCode,
    onSubmit,
    onAccountPromoCodeVerify,
    onAccountUsernameVerify,
    onAccountEmailVerify,
    onAccountPhoneNumberVerify,
    onGoBack,
    defaultPromoCode,
    defaultProPromoCode: WALDO_PRO_FREE_PROMO_CODE,
    personaDisabled: !!plic,
    noMarketing,
    skipFreeTrial: skipFreeTrialBool,
    skipPhotoFinder: skipPhotoFinderBool,
    isWaldoProFreeRegister,
    verifyingDefaultPromoCode,
    onEmailPinCodeRequest,
    onPasswordCreate,
    onPhotoManagerPurchaseReminderSet,
  };
};

export default useProRegister;
